import React, { createContext, useContext, useState } from "react";
import { useFetchOncePerUser } from "../hooks/useFetchOncePerUser";
import { useAuth } from "./AuthContext";
import {
  getAllVendors,
  createVendor,
  updateVendor as updateVendorService,
  deleteVendor as deleteVendorService,
} from "../api/services/vendorService";

const VendorContext = createContext();

export const VendorProvider = ({ children }) => {
  const [vendors, setVendors] = useState([]);
  const { user } = useAuth();

  // `function` declaration so it's hoisted for useFetchOncePerUser below.
  async function fetchVendors() {
    try {
      const response = await getAllVendors();
      // Extract the data array from the response object
      const vendorsData = response.data || response || [];
      setVendors(Array.isArray(vendorsData) ? vendorsData : []);
    } catch (error) {
      console.error("Error fetching vendors:", error);
      setVendors([]); // Set empty array as fallback
    }
  }

  // Fetch vendors when a user first appears / changes identity.
  useFetchOncePerUser(user, fetchVendors);

  const addVendor = async (vendorData) => {
    try {
      const data = await createVendor(vendorData);
      setVendors([...vendors, data]);
      return data;
    } catch (error) {
      console.error("Error adding vendor:", error);
      throw error;
    }
  };

  const updateVendor = async (vendorId, updatedData) => {
    try {
      const data = await updateVendorService(vendorId, updatedData);
      setVendors(
        vendors.map((vendor) => (vendor._id === vendorId ? data : vendor))
      );
      return data;
    } catch (error) {
      console.error("Error updating vendor:", error);
      throw error;
    }
  };

  const deleteVendor = async (vendorId) => {
    try {
      await deleteVendorService(vendorId);
      setVendors(vendors.filter((vendor) => vendor._id !== vendorId));
    } catch (error) {
      console.error("Error deleting vendor:", error);
      throw error;
    }
  };

  return (
    <VendorContext.Provider
      value={{
        vendors,
        addVendor,
        updateVendor,
        deleteVendor,
        fetchVendors,
      }}
    >
      {children}
    </VendorContext.Provider>
  );
};

export const useVendor = () => {
  const context = useContext(VendorContext);
  if (!context) {
    throw new Error("useVendor must be used within a VendorProvider");
  }
  return context;
};
